import { useAtomValue } from 'jotai';
import { Loader2 } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import {
  bulkSyncCumulativeProcessedAtom,
  bulkSyncExpectedUploadTotalAtom,
  bulkSyncPhaseAtom,
  bulkSyncUploadProgressAtom,
} from '@/atoms/chronicle/bulk-sync';
import { useIsDesktopChronicleEnabled } from '@/features/flags';
import {
  TypographyCaptionBold,
  TypographyFootNote,
} from '@heidi/design-system/typography';

export const SessionCreationInProgressBanner = () => {
  const isChronicleEnabled = useIsDesktopChronicleEnabled();
  const phase = useAtomValue(bulkSyncPhaseAtom);
  const uploadProgress = useAtomValue(bulkSyncUploadProgressAtom);
  const expectedUploadTotal = useAtomValue(bulkSyncExpectedUploadTotalAtom);
  const cumulativeProcessed = useAtomValue(bulkSyncCumulativeProcessedAtom);

  // Gate by Chronicle desktop flag — keeps the banner out of web builds
  // and accounts without the Heidi Remote feature.
  if (!isChronicleEnabled || phase !== 'uploading') return null;

  // Denominator = device-discovered total; numerator = run-cumulative uploaded
  // count (not the engine's per-batch `uploadProgress.completedFiles`, which
  // resets each sequentially-synced recording → would read "1/2" during the 2nd).
  const totalSessions = Math.max(expectedUploadTotal, uploadProgress.totalFiles);
  const completedSessions = Math.min(cumulativeProcessed, totalSessions);
  const currentSession = Math.min(completedSessions + 1, totalSessions);

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="session-creation-in-progress-banner"
      className="flex shrink-0 items-center gap-3 border-b border-border bg-surface-elevated px-4 py-3"
    >
      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-background-secondary">
        <Loader2
          className="size-4 text-text-primary motion-safe:animate-spin"
          strokeWidth={2.2}
        />
      </div>
      <div className="flex min-w-0 flex-col">
        <TypographyCaptionBold className="truncate text-text-primary">
          <FormattedMessage defaultMessage="Processing sessions" id="gFIcOdu8Bp" />
        </TypographyCaptionBold>
        {totalSessions > 0 && (
          <TypographyFootNote className="text-text-secondary">
            <FormattedMessage
              defaultMessage="{current}/{total}"
              id="KQd37jzhqv"
              values={{ current: currentSession, total: totalSessions }}
            />
          </TypographyFootNote>
        )}
      </div>
    </div>
  );
};
